import finioDashboard from "../Assets/Projects/finio.png";
import medeasePortal from "../Assets/Projects/medease.png";
import machineLearning from "../Assets/Projects/machine-learning.png";
import smartHomeForElderPeople from "../Assets/Projects/smart-home-for-elder-people.png";
import swasthya from "../Assets/Projects/swasthya.png";
import doctorMedicationSystem from "../Assets/Projects/doctor-medication-system.png";
import faceDetectorApi from "../Assets/Projects/face-detector-api.png";
import maanak from "../Assets/Projects/maanak.png";
import nestiqRealEstate from "../Assets/Projects/nestiq.png";
import saffronRestaurant from "../Assets/Projects/saffron-dashboard.png";
import vidyaEdtech from "../Assets/Projects/vidya.png";

const projectFocusLabels = {
  healthcare: "Healthcare web apps",
  operations: "Dashboards and internal tools",
  integrations: "Supporting integrations",
  verification: "Specialized web workflows",
};

const legacyProjectFocusMap = {
  ai: "integrations",
};

function humanizeProjectFocusId(focusId) {
  if (!focusId) {
    return "";
  }

  const normalizedLabel = String(focusId)
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

  return normalizedLabel.charAt(0).toUpperCase() + normalizedLabel.slice(1);
}

export const projectFocusOptions = [
  { id: "all", label: "All selected work" },
  { id: "healthcare", label: projectFocusLabels.healthcare },
  { id: "operations", label: projectFocusLabels.operations },
  { id: "integrations", label: projectFocusLabels.integrations },
  { id: "verification", label: projectFocusLabels.verification },
];

export function normalizeProjectFocus(focusId) {
  return legacyProjectFocusMap[focusId] || focusId;
}

export function getProjectFocusLabel(focusId) {
  const normalizedFocusId = normalizeProjectFocus(focusId);

  return (
    projectFocusLabels[normalizedFocusId] ||
    humanizeProjectFocusId(normalizedFocusId)
  );
}

const projectData = [
  {
    slug: "saffron-restaurant",
    featured: true,
    featuredLabel: "Restaurant & hospitality",
    focusAreas: ["hospitality", "consumer"],
    clientFit:
      "Restaurant owners and hospitality businesses that need a premium booking experience and digital menu presence.",
    inquiryType: "Marketing site, booking system, or consumer web app",
    imgPath: saffronRestaurant,
    isBlog: false,
    title: "Saffron — Fine Indian Dining",
    description:
      "Upscale restaurant website with a 3-step table booking wizard, interactive menu with category filters, and booking confirmation with calendar download.",
    stack: ["React", "Node.js", "PostgreSQL", "Framer Motion", "Tailwind CSS"],
    ownership: [
      "End-to-end product design",
      "Booking flow UX",
      "Full-stack delivery",
    ],
    caseStudy: {
      problem:
        "Restaurants lose reservations to friction — clunky forms, no time slot visibility, and no confirmation users can trust.",
      delivery:
        "Built a 3-step wizard that handles date selection, time slot availability, guest details, and confirmation in one smooth flow. Backed by a real PostgreSQL database with live slot checks.",
      outcome:
        "Shows how I turn a hospitality booking problem into a polished consumer experience — not just a form, but a product moment.",
    },
    highlights: [
      "Live table availability backed by a real database — not a static mockup.",
      "Booking confirmation generates a downloadable ICS calendar file.",
      "Dark editorial aesthetic with gold accents built entirely in Tailwind and Framer Motion.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/saffron-restaurant",
    demoLink: "https://saffron.iomjaiswal.com/",
    demoLabel: "Live site",
  },

  {
    slug: "finio-finance-dashboard",
    featured: true,
    featuredLabel: "Fintech & data dashboards",
    focusAreas: ["fintech", "dashboard", "data"],
    clientFit:
      "Fintech startups or SaaS teams that need a data-rich dashboard with charts, budget tracking, and financial reporting.",
    inquiryType: "Dashboard, SaaS product, or internal tool",
    imgPath: finioDashboard,
    isBlog: false,
    title: "Finio — Personal Finance Dashboard",
    description:
      "Dark-mode fintech dashboard with transaction ledger, budget tracking, savings goals, spending trend charts, and a monthly savings report with CSV export.",
    stack: [
      "React",
      "Recharts",
      "Node.js",
      "PostgreSQL",
      "React Query",
      "Tailwind CSS",
    ],
    ownership: [
      "Full-stack product delivery",
      "Data visualization design",
      "Dashboard UX architecture",
    ],
    caseStudy: {
      problem:
        "Financial data products often drown users in numbers. The challenge is surfacing what matters — trends, over-budget signals, goal progress — without noise.",
      delivery:
        "Built 5 interconnected pages: an overview with sparkline stat cards, a transaction ledger with infinite scroll and filters, a budget tracker with visual progress bars, a goals page with circular progress rings, and a reports page with category deep-dive charts.",
      outcome:
        "Demonstrates how I architect a data-heavy product where every screen has a clear job, and the visual design makes numbers readable rather than overwhelming.",
    },
    highlights: [
      "Count-up number animations and sparkline charts on every stat card.",
      "Budget over-spend alerts with pulsing red indicators.",
      "CSV export on the reports page — a feature clients actually ship.",
      "Recharts area, bar, and line charts with custom tooltips throughout.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/finio-dashboard",
    demoLink: "https://finio.iomjaiswal.com/",
    demoLabel: "Live site",
  },

  {
    slug: "vidya-edtech-platform",
    featured: false,
    featuredLabel: "EdTech & learning platforms",
    focusAreas: ["edtech", "consumer", "platform"],
    clientFit:
      "EdTech startups or course creators who need a learner-facing platform with course discovery, video lessons, and student progress tracking.",
    inquiryType: "Platform, marketplace, or consumer product",
    imgPath: vidyaEdtech,
    isBlog: false,
    title: "Vidyā — Online Learning Platform",
    description:
      "Indian EdTech platform with course listings, video player with notes and transcript tabs, accordion syllabus, instructor profiles, and a student dashboard with streak tracking.",
    stack: ["React", "Node.js", "PostgreSQL", "Framer Motion", "Tailwind CSS"],
    ownership: [
      "Learner experience design",
      "Course flow architecture",
      "Full-stack delivery",
    ],
    caseStudy: {
      problem:
        "Most learning platforms feel like content dumps. The real design challenge is making discovery feel curated and lessons feel worth completing.",
      delivery:
        "Built course listing with filters and instructor cards, a detail page with accordion syllabus and sticky enrollment sidebar, and a learning page combining video, notes, and transcript in one tabbed interface. Student dashboard shows progress and a daily streak heatmap.",
      outcome:
        "Shows how I handle a multi-role platform — browsing, learning, and progress — each screen with its own interaction model and visual language.",
    },
    highlights: [
      "Learning page combines video player, notes editor, and transcript in a single tabbed layout.",
      "Student dashboard with a GitHub-style activity heatmap for daily streaks.",
      "INR pricing and Indian instructor profiles for authentic market context.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/vidya-edtech",
    demoLink: "https://vidya.iomjaiswal.com/",
    demoLabel: "Live site",
  },

  {
    slug: "medease-doctor-portal",
    featured: false,
    featuredLabel: "Healthcare & booking platforms",
    focusAreas: ["healthcare", "consumer", "booking"],
    clientFit:
      "Healthcare startups or clinic networks that need a patient-facing doctor discovery and appointment booking product.",
    inquiryType: "Booking platform, healthcare app, or patient portal",
    imgPath: medeasePortal,
    isBlog: false,
    title: "MedEase — Doctor Booking Portal",
    description:
      "Patient-facing doctor discovery platform with 9 search filters, doctor profiles with specialty and availability, a 3-step appointment wizard, and an appointment ticket with no login required.",
    stack: [
      "React",
      "Node.js",
      "PostgreSQL",
      "React Hook Form",
      "Tailwind CSS",
    ],
    ownership: [
      "Patient experience design",
      "Search and filter UX",
      "Full-stack delivery",
    ],
    caseStudy: {
      problem:
        "Booking a doctor online usually means navigating an overwhelming list with no way to filter by what actually matters — specialty, city, fee, availability.",
      delivery:
        "Built a doctor search page with 9 simultaneous filters (specialty, city, gender, experience, fee range, language, availability, rating, consultation type), a doctor profile with sticky booking sidebar, and a 3-step wizard that produces a printable appointment confirmation. No account required — lookup by phone number.",
      outcome:
        "Shows how I handle a high-filter search product and guide users from discovery to confirmed appointment in a way that feels fast, not clinical.",
    },
    highlights: [
      "9 simultaneous search filters with real-time result count.",
      "Appointment confirmation ticket design — printable, shareable.",
      "No login required — appointments looked up by phone number only.",
      "20 doctors across Mumbai, Delhi, Bangalore, and Pune with realistic profiles.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/medease-portal",
    demoLink: "https://medease.iomjaiswal.com/",
    demoLabel: "Live site",
  },

  {
    slug: "nestiq-real-estate",
    featured: true,
    featuredLabel: "Real estate & property platforms",
    focusAreas: ["real-estate", "marketplace", "consumer"],
    clientFit:
      "PropTech startups or real estate agencies that need a property discovery platform with search, filters, and lead capture.",
    inquiryType: "Marketplace, listing platform, or consumer web app",
    imgPath: nestiqRealEstate,
    isBlog: false,
    title: "Nestiq — Property Listings Platform",
    description:
      "Real estate discovery platform with multi-filter property search, gallery lightbox, live EMI calculator, wishlist, price trend charts, and an inquiry form — across 5 Indian metros.",
    stack: [
      "React",
      "Recharts",
      "Node.js",
      "PostgreSQL",
      "Framer Motion",
      "Tailwind CSS",
    ],
    ownership: [
      "Full marketplace UX",
      "Search and filter architecture",
      "Full-stack delivery",
    ],
    caseStudy: {
      problem:
        "Property platforms overwhelm buyers with listings and underwhelm them with tools. Users need to filter quickly, evaluate seriously, and act confidently.",
      delivery:
        "Built a full discovery-to-inquiry flow: a home page with hero search, a listings page with 9 concurrent filters and grid/list toggle, a detail page with image gallery lightbox and price history chart, a live EMI calculator in the sidebar, and a wishlist persisted in localStorage.",
      outcome:
        "Shows how I handle a complex marketplace product — high-density filtering, rich detail pages, and lead capture — without the UI becoming a spreadsheet.",
    },
    highlights: [
      "Dual-handle range sliders for price and area — built from scratch, no library.",
      "Live EMI calculator with 3 sliders updating monthly payment in real time.",
      "Price history chart (6 months) using Recharts on every property detail page.",
      "Wishlist with localStorage persistence and a shareable URL.",
      "24 seeded properties across Mumbai, Bangalore, Delhi, Pune, and Hyderabad.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/nestiq-real-estate",
    demoLink: "https://nestiq.iomjaiswal.com/",
    demoLabel: "Live site",
  },
  {
    slug: "doctor-medication-system",
    featured: true,
    featuredLabel: "Healthcare operations",
    focusAreas: ["healthcare", "operations"],
    clientFit:
      "Teams that need a staff-facing healthcare workflow with clear records and medication coordination.",
    inquiryType: "Web app, client portal, or dashboard",
    imgPath: doctorMedicationSystem,
    isBlog: false,
    title: "Doctor Medication System",
    description:
      "Healthcare web app for managing doctors, patients, and medication workflows through a clear staff-facing interface.",
    stack: [
      "Staff-facing interface",
      "Care workflow screens",
      "Structured forms",
      "Role-based views",
    ],
    ownership: [
      "Product UX",
      "Workflow modeling",
      "Operational interface delivery",
    ],
    caseStudy: {
      problem:
        "Doctor, patient, and medication activity needs one dependable workflow instead of fragmented records and admin friction.",
      delivery:
        "Structured the core relationships, shaped the day-to-day operational screens, and built the product so routine actions stay clear for staff.",
      outcome:
        "Shows how I turn a multi-actor healthcare process into a usable system rather than shipping a thin CRUD surface.",
    },
    highlights: [
      "Structured complex doctor, patient, and medication flows into one operational system.",
      "Included a walkthrough link so clients can judge the product experience directly.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/DoctorMedicationSystem.git",
    demoLink:
      "https://drive.google.com/file/d/1oYStkCZKLq9oQy8RMPlgrpMzgp3i7VZN/view?usp=sharing",
    demoLabel: "Video walkthrough",
  },
  {
    slug: "smart-home-for-elder-people",
    featured: true,
    featuredLabel: "Care-monitoring product",
    focusAreas: ["healthcare", "integrations"],
    clientFit:
      "Care-monitoring products that need a trustworthy caregiver flow and clearer alert handling.",
    inquiryType: "Website or web app with supporting integration",
    imgPath: smartHomeForElderPeople,
    isBlog: false,
    title: "Smart Home for Elder People",
    description:
      "Care-monitoring product interface that turns unusual activity signals into a clearer caregiver-facing experience.",
    stack: [
      "Caregiver experience",
      "Monitoring alerts",
      "Responsive screens",
      "Decision-support states",
    ],
    ownership: [
      "Caregiver workflow UX",
      "Monitoring experience",
      "Integrated product flow",
    ],
    caseStudy: {
      problem:
        "Monitoring data only becomes useful when caregivers can notice unusual behavior quickly and act with confidence.",
      delivery:
        "Combined monitoring signals with a caregiver-facing web experience so the workflow supports supervision instead of exposing raw system output alone.",
      outcome:
        "Demonstrates that I treat supporting product logic as part of a caregiver experience, not as the whole product story.",
    },
    highlights: [
      "Focused on caregiver visibility rather than raw model output alone.",
      "Combined monitoring logic with a usable caregiver experience for day-to-day supervision.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/SmartHomeForElderPeople",
  },
  {
    slug: "hypertension-detector",
    featured: false,
    featuredLabel: "Preventive health screening",
    focusAreas: ["healthcare", "integrations"],
    clientFit:
      "Preventive-health tools that need clearer risk screening and decision support.",
    inquiryType: "Website or web app with supporting integration",
    imgPath: machineLearning,
    isBlog: false,
    title: "Hypertension Detector",
    description:
      "Healthcare screening web app focused on earlier hypertension risk visibility and clearer patient monitoring.",
    stack: [
      "Screening journey",
      "Risk summary UI",
      "Guided form flow",
      "Decision-support moments",
    ],
    ownership: [
      "Screening experience",
      "Risk-visibility flow",
      "Integrated product support",
    ],
    caseStudy: {
      problem:
        "Early hypertension screening is only useful if the risk signal is clear enough to support preventive action instead of feeling like a detached experiment.",
      delivery:
        "Built a screening workflow around the underlying evaluation, shaped the data-entry flow, and focused the product on earlier risk visibility and practical monitoring.",
      outcome:
        "Shows how I package decision support into a preventive-health product flow where clarity and intervention matter as much as the result itself.",
    },
    highlights: [
      "Applied predictive screening to a concrete preventive-health use case instead of a generic demo scenario.",
      "Framed the product around earlier detection and clearer monitoring support.",
    ],
    ghLink: "https://github.com/iOMJaiswal/HypertensionDetectorML",
  },
  {
    slug: "maanak-aqar-record-management",
    featured: false,
    featuredLabel: "University operations",
    focusAreas: ["operations"],
    clientFit:
      "Operational record systems that replace fragmented manual documentation with one dependable workflow.",
    inquiryType: "Web app, client portal, or dashboard",
    imgPath: maanak,
    isBlog: false,
    title: "Maanak AQAR Record Management",
    description:
      "University record management system built to simplify NAAC documentation and reduce manual record handling.",
    stack: [
      "Admin interface",
      "Record workflow",
      "Responsive forms",
      "Structured dashboards",
    ],
    ownership: [
      "Workflow design",
      "Admin interface design",
      "Record-system structure",
    ],
    caseStudy: {
      problem:
        "NAAC preparation and university record handling often depend on scattered files, manual coordination, and slow handoffs between staff.",
      delivery:
        "Built a structured record-management workflow that centralizes documentation, organizes submissions, and gives staff one dependable place to maintain required records.",
      outcome:
        "Demonstrates operational thinking for admin-heavy products where reliability, structure, and lower coordination overhead matter more than flashy UI.",
    },
    highlights: [
      "Solved a real administrative workflow instead of presenting a generic CRUD app.",
      "Reduced documentation friction by centralizing records in a structured system.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/Maanak",
  },
  {
    slug: "swasthya-emergency-web-app",
    featured: true,
    featuredLabel: "Emergency care access",
    focusAreas: ["healthcare"],
    clientFit:
      "Healthcare products that need urgent guidance and service access in one calmer user flow.",
    inquiryType: "Web app, client portal, or dashboard",
    imgPath: swasthya,
    isBlog: false,
    title: "Swasthya Emergency Web App",
    description:
      "Emergency healthcare web app centralizing first-aid guidance, ambulance requests, and doctor appointments in one accessible flow.",
    stack: [
      "Responsive web UI",
      "Urgent-care flow",
      "Action-first screens",
      "Service coordination",
    ],
    ownership: [
      "Emergency UX flow",
      "Service aggregation",
      "Responsive web experience",
    ],
    caseStudy: {
      problem:
        "In urgent health situations, people need fast guidance and access to help, not a fragmented set of separate services and confusing next steps.",
      delivery:
        "Combined first-aid guidance, ambulance requests, and appointment actions into one calmer interface built around urgent access and decision support.",
      outcome:
        "Shows how I simplify higher-stress healthcare journeys by unifying critical actions into a clearer product flow.",
    },
    highlights: [
      "Bundled several urgent-care actions into one more accessible experience.",
      "Designed around moments where clarity matters more than visual complexity.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/Swasthya",
  },
  {
    slug: "face-detector-api",
    featured: false,
    featuredLabel: "Verification product flow",
    focusAreas: ["verification", "integrations"],
    clientFit:
      "Specialized web workflows that need validation logic, clear pass-fail feedback, and dependable integration behavior.",
    inquiryType: "Website or web app with supporting integration",
    imgPath: faceDetectorApi,
    isBlog: false,
    title: "Face Verification Workflow",
    description:
      "Verification workflow prototype that checks bank-defined face capture rules and returns a clear pass-fail result.",
    stack: [
      "Capture guidance UI",
      "Validation feedback",
      "Pass-fail states",
      "Workflow rules",
    ],
    ownership: [
      "Workflow logic",
      "Validation experience",
      "Integrated product flow",
    ],
    caseStudy: {
      problem:
        "Verification flows need explicit rules and predictable pass-fail behavior, not just image checks in isolation.",
      delivery:
        "Designed the validation workflow around bank-defined capture rules, image checks, and clearer pass-fail feedback so the result made sense inside a real product flow.",
      outcome:
        "Shows how integration-heavy product logic can still be shaped into a clearer frontend workflow instead of a detached technical demo.",
    },
    highlights: [
      "Built for verification workflows with explicit input rules and pass-fail output.",
      "Shows how rule-heavy validation can be packaged into a clearer product experience.",
    ],
    // ghLink: "https://github.com/iOMJaiswal/Face-Detector-Rest-API",
  },
];

export default projectData;
